import React, { useEffect, useMemo, useRef, useState } from "react";
import micUrl from "@/assets/interview/controls/mic.svg";
import AnalyzingTextScanner from "./AnalyzeText";
import AudioNotFound from "./AudioNotFound";

type MicMode = "listening" | "processing" | "ai_speaking" | "empty_audio" | "idle";

interface MicControlsProps {
  mode?: MicMode;
  analyzeText?: string;
  liveTranscript?: string;
  onDone?: () => void;
  noticeText?: string | null;
  showInlineTranscript?: boolean;
  inlineTranscriptMaxLines?: 2 | 3;
  disabled?: boolean;
  doneLabel?: string;
}

const MicControls: React.FC<MicControlsProps> = ({ mode = "listening", analyzeText = "", liveTranscript = "", onDone, noticeText = null, showInlineTranscript = false, inlineTranscriptMaxLines = 2, disabled = false, doneLabel = "Done Answering" }) => {
  const [capturedTranscript, setCapturedTranscript] = useState<string>("");

  useEffect(() => {
    if (mode === "ai_speaking") {
      setCapturedTranscript("");
    }
  }, [mode]);

  useEffect(() => {
    // Keep a copy for Analyze view when transitioning away from listening
    if (mode === "listening") {
      setCapturedTranscript(liveTranscript);
    }
  }, [liveTranscript, mode]);

  const handleDone = () => {
    if (onDone) onDone();
  };
  // Prepare compact inline transcript base (latest words, optional leading ellipsis)
  const inlineTranscriptBase = useMemo(() => {
    if (!showInlineTranscript || mode !== "listening") return "";
    const cleaned = (liveTranscript || "").replace(/\s+/g, " ").trim();
    if (!cleaned) return "";

    // Keep the last two sentences when possible; fallback to last ~20 words
    const sentences = cleaned.match(/[^.!?]+[.!?]?/g)?.map((s) => s.trim()).filter(Boolean) ?? [];
    const tailSentences = sentences.slice(-2);
    const sentenceText = tailSentences.join(" ").trim();
    if (sentenceText) return sentenceText;

    const words = cleaned.split(" ").filter(Boolean);
    if (words.length === 0) return "";
    const TAIL_WORDS = 20;
    return words.slice(-TAIL_WORDS).join(" ");
  }, [showInlineTranscript, mode, liveTranscript, inlineTranscriptMaxLines]);

  // Ensure the inline transcript always fits exactly within the desired number of lines (no trailing ellipsis)
  const [displayInlineTranscript, setDisplayInlineTranscript] = useState<string>("");
  const pRef = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    if (!showInlineTranscript || mode !== "listening") {
      setDisplayInlineTranscript("");
      return;
    }
    const el = pRef.current;
    if (!el) {
      setDisplayInlineTranscript(inlineTranscriptBase);
      return;
    }

    const fitToLines = (text: string): string => {
      // Early set to measure
      el.textContent = text;
      const style = window.getComputedStyle(el);
      let lineHeight = parseFloat(style.lineHeight || "0");
      if (!isFinite(lineHeight) || lineHeight <= 0) {
        const fontSize = parseFloat(style.fontSize || "16");
        lineHeight = fontSize * 1.4; // fallback approximation
      }
      const maxLines = inlineTranscriptMaxLines || 2;
      const fits = () => Math.ceil(el.scrollHeight / lineHeight) <= maxLines;

      if (fits()) return text;

      // Binary search: drop leading words until fits
      const raw = text.replace(/^…\s*/, "");
      const words = raw.split(" ").filter(Boolean);
      let lo = 0, hi = words.length; // number of words to drop
      let best = text;
      while (lo < hi) {
        const mid = Math.floor((lo + hi) / 2);
        const candidate = (mid > 0 ? `… ${words.slice(mid).join(" ")}` : words.join(" "));
        el.textContent = candidate;
        if (fits()) {
          best = candidate;
          hi = mid;
        } else {
          lo = mid + 1;
        }
      }
      const finalText = (lo > 0 ? `… ${words.slice(lo).join(" ")}` : words.join(" "));
      el.textContent = finalText;
      return finalText;
    };

    // Use rAF to ensure layout is up to date before measuring
    let raf = requestAnimationFrame(() => {
      const fitted = fitToLines(inlineTranscriptBase);
      setDisplayInlineTranscript(fitted);
    });
    return () => cancelAnimationFrame(raf);
  }, [inlineTranscriptBase, inlineTranscriptMaxLines, showInlineTranscript, mode]);

  // Refit on window resize to keep 2-line guarantee
  useEffect(() => {
    const onResize = () => {
      if (!showInlineTranscript || mode !== "listening") return;
      const el = pRef.current;
      if (!el) return;
      // Trigger recompute by resetting base
      const style = window.getComputedStyle(el);
      // noop read to sync layout
      void style.width;
      // Re-run fitting by setting dependency via state
      setDisplayInlineTranscript(prev => prev); // noop to keep
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [showInlineTranscript, mode]);

  // Show analyzer only when processing AND not disabled
  // When disabled during processing (e.g., tool call like problem change), show disabled mic controls instead
  if (mode === "processing" && !disabled) {
    return <AnalyzingTextScanner initialText={analyzeText || capturedTranscript} />
  }

  if (!disabled && mode === "empty_audio") {
    return <AudioNotFound text={noticeText || undefined} />
  }

  return (
    <div className={`flex h-full w-full items-center justify-center ${disabled ? 'bg-gray-100 opacity-50 cursor-not-allowed' : 'bg-[rgba(31,209,103,0.05)]'}
`}>
      <div className={`relative mt-2 flex w-full max-w-[360px] flex-col items-center justify-center py-8 px-6 md:h-[40%] ${disabled ? 'pointer-events-none'
: ''}`}>
        {/* Inline live transcript (small, responsive) shown only when listening */}
        {displayInlineTranscript && (
          <div className="w-full mb-3 px-1">
            <p
              className="text-left font-dm-sans leading-6 md:leading-[14px] lg:leading-[16px] xl:leading-[22px] text-base md:text-[9px] lg:text-[12px] xl:text-[15px] text-slate-700 italic"
              style={{
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
              }}
              ref={pRef}
            >
              {displayInlineTranscript}
            </p>
          </div>
        )}

        <div className="flex w-full max-w-[350px] flex-col items-center gap-4 md:gap-2 lg:gap-2.5">
          <div className="flex gap-4 md:gap-2 lg:gap-2.5 flex-row items-center justify-center 
            w-full max-w-[250px] md:w-[100px] lg:w-[120px] xl:w-[140px] 2xl:w-[160px]
            h-12 md:h-7 lg:h-9 xl:h-10 2xl:h-12
            px-4 md:px-1
            bg-white rounded-sm md:text-[9px] lg:text-[10px]">
            <img src={micUrl} className="w-10 h-10 md:w-6 md:h-8 lg:w-7 lg:h-7" />
            <p className="text-center font-ubuntu  text-[16px] md:text-[8px] lg:text-[10px] xl:text-[12px] 2xl:text-[14px] text-slate-900">Listening</p>
          </div>
          <button
            type="button"
            onClick={handleDone}
            disabled={disabled}
            className="w-full cursor-pointer max-w-[250px] md:w-[100px] lg:w-[120px] xl:w-[140px] 2xl:w-[160px] 
             h-12 md:h-7 lg:h-9 xl:h-10 2xl:h-12
             bg-slate-900 rounded-sm border border-button text-center text-white 
             text-[16px] md:text-[8px] lg:text-[10px] xl:text-[12px] 2xl:text-[14px] 
             font-medium leading-tight transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {doneLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MicControls;
